import { Account } from "api/mappers/accountMapper";
import imageCompression from "browser-image-compression";
import { useCallback, useState } from "react";
import { useQuery } from "react-query";
import { DISCOUNT_TIER } from "api/interfaces/subscription.interface";
import {
  getAccuntOwnersReponse,
  ProfileAccountInfo,
} from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import {
  CustomError,
  LEVEL_TYPE,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import useStatusMutation from "hook/useStatusMutation";
import { putAccountUpdate, getAccountDetail } from "api/accountAPI";
import useApiError from "hook/useApiError";
import { MYDW_LOGO } from "components/blocks/sidenav/TopInfo";
import { enableImageTypesOption, Option } from "utils/options";
import { AccountDetailPresenter } from "./AccountDetailPresenter";
import {
  getIsBillingTypeDisabled,
  handleDuplicateAccountName,
  isDiscountEmpty,
} from "utils/AccountUtil";
import { getAccountOwners } from "api/userAPI";
import { selectStomp } from "redux/stomp/stompSelector";
import { useSelector } from "react-redux";
import { useMenu } from "hook/useMenu";
import { getAccountNameValidationError } from "./AccountInner";
import { changeLevelFromABBToABB, toTrim } from "utils/functions";
import { BILLING_TYPE } from "api/interfaces/planInterface.interface";

export enum UPDATE_TYPE {
  NAME,
  LEVEL,
  TIMEZONE,
  DATE_FORMAT,
  TIME_FORMAT,
  ADDITIONAL_ACCOUNT_NUMBER,
  PRICING,
  TOW_FACTOR_AUTH,
  DESC,
  OWNER,
  COMPANY_NAME,
  COMPANY_LOCATION,
  BILLING_TYPE,
}

interface Props {
  isMyAccount?: boolean;
  onModal?: (type?: string) => void;
  accountId: string;
  enableTransfer?: boolean;
  onSuccessEditAccount?: () => void;
}

export interface EditAccountInfo
  extends Omit<
    Account,
    | "levelFullName"
    | "hasAccounts"
    | "enableTransfer"
    | "hasRecorders"
    | "hasCameras"
    | "hasUsers"
    | "parentAccount"
    | "isFavorite"
    | "ownerName"
    | "ownerEmail"
    | "isSuspended"
    | "isActive"
    | "enableEdit"
  > {}

const initialEditAccountInfo: EditAccountInfo = {
  accountId: "",
  parentId: "",
  parentLevel: LEVEL_TYPE.INTL,
  accountAdditionalNumber: "",
  name: "",
  level: LEVEL_TYPE.INTL,
  accountNumber: "",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // @todo: enum
  dateFormat: "",
  timeFormat: "",
  use2faAuth: false,
  companyName: "",
  companyLocation: "",
  companyLogo: MYDW_LOGO,
  description: "",
  discountTier: DISCOUNT_TIER.CUSTOM,
  discount: 0,
  creditLimit: undefined,
  cascadedSuspension: 0,
  createdAt: "",
  billingType: BILLING_TYPE.UNKNOWN,
  isPayment: false,
};

export function AccountDetailContainer(props: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const addBaseLevel = changeLevelFromABBToABB({
    level: selectedAccount.accountLevel as LEVEL_TYPE,
    parentLevel: selectedAccount.parentLevel as LEVEL_TYPE,
  });

  const { updateCurrentAccount } = useMenu({});

  const { handleErrorNotify, notifySuccess, notifyError, lang, intlString } =
    useApiError();

  const [validateMsg, setValidateMsg] = useState<ValidateMsg>({
    name: "",
    discount: "",
  });
  const { needUpdateCurrentAccount } = useSelector(selectStomp);

  const [editAccountInfo, setEditAccountInfo] = useState<EditAccountInfo>({
    ...initialEditAccountInfo,
  });
  const [logoImageFile, setLogoImageFile] = useState<File>();
  const [logoImageUrl, setLogoImageUrl] = useState<string>(MYDW_LOGO);
  const [accountAdmins, setAccountAdmins] = useState<Option[]>([]);

  const {
    data,
    isFetching: loading,
    refetch,
  } = useQuery(
    ["accountDetail", props.accountId, needUpdateCurrentAccount],
    () => getAccountDetail(props.accountId),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      enabled: !!props.accountId,
      onSuccess: (res: any) => {
        if (res.result) {
          const isBillingTypeDisabled = getIsBillingTypeDisabled({
            isUsagePaid: selectedAccount.isUsagePaid,
            accountInfo: res.result,
          });
          setEditAccountInfo({
            ...res.result,
            parentLevel:
              selectedAccount.accountLevel === LEVEL_TYPE.DW
                ? "DW"
                : res.result.parentLevel,
            billingType:
              isBillingTypeDisabled ||
              !(res.result.planAutoEnroll && res.result.isPayment)
                ? BILLING_TYPE.KEY
                : BILLING_TYPE.USAGE,
          });
          setLogoImageUrl(res.result.companyLogo ?? MYDW_LOGO);
        }
      },
      onError: () => {
        setEditAccountInfo(initialEditAccountInfo);
        setLogoImageUrl(MYDW_LOGO);
      },
    }
  );

  const accountInfo = data?.result;

  const { isFetching: accountOwnerIsFetching } = useQuery(
    ["accountOwners", props.accountId, accountInfo?.ownerId],
    () => getAccountOwners(props.accountId),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      enabled:
        !!props.accountId &&
        !!accountInfo?.ownerId &&
        selectedAccount.accountId === props.accountId,
      onSuccess: (res: getAccuntOwnersReponse) => {
        if (res.error !== 0) {
          setAccountAdmins([]);
          return;
        }
        const admins = res.result.map((owner): Option => {
          return {
            value: owner.id,
            label: `${owner.name} (${owner.email})`,
          };
        });
        if (
          admins.findIndex((admin) => admin.value === accountInfo?.ownerId) ===
          -1
        ) {
          admins.push({
            value: selectedAccount?.ownerId || "",
            label: `${selectedAccount?.ownerName} (${selectedAccount?.ownerEmail})`,
            isDisabled: true,
          });
        }
        setAccountAdmins(admins);
      },
      onError: () => {
        setAccountAdmins([]);
      },
    }
  );

  const onChangeAccountInfo = useCallback(
    (type: UPDATE_TYPE, value: number | string | boolean) => {
      if (type === UPDATE_TYPE.NAME) {
        setEditAccountInfo((info) => {
          return { ...info, name: value as string };
        });
        setValidateMsg((msg) => ({ ...msg, name: "" }));
      } else if (type === UPDATE_TYPE.DATE_FORMAT) {
        setEditAccountInfo((info) => {
          return { ...info, dateFormat: value as string };
        });
      } else if (type === UPDATE_TYPE.TIME_FORMAT) {
        setEditAccountInfo((info) => {
          return { ...info, timeFormat: value as string };
        });
      } else if (type === UPDATE_TYPE.TIMEZONE) {
        setEditAccountInfo((info) => {
          return { ...info, timezone: value as string };
        });
      } else if (type === UPDATE_TYPE.COMPANY_NAME) {
        setEditAccountInfo((info) => {
          return { ...info, companyName: value as string };
        });
      } else if (type === UPDATE_TYPE.COMPANY_LOCATION) {
        setEditAccountInfo((info) => {
          return { ...info, companyLocation: value as string };
        });
      } else if (type === UPDATE_TYPE.TOW_FACTOR_AUTH) {
        setEditAccountInfo((info) => {
          return { ...info, use2faAuth: value as boolean };
        });
      } else if (type === UPDATE_TYPE.ADDITIONAL_ACCOUNT_NUMBER) {
        setEditAccountInfo((info) => {
          return { ...info, accountAdditionalNumber: value as string };
        });
      } else if (type === UPDATE_TYPE.DESC) {
        setEditAccountInfo((info) => {
          return { ...info, description: value as string };
        });
      } else if (type === UPDATE_TYPE.OWNER) {
        setEditAccountInfo((info) => {
          return { ...info, ownerId: value as string };
        });
      } else if (type === UPDATE_TYPE.BILLING_TYPE) {
        if (props.isMyAccount || addBaseLevel !== LEVEL_TYPE.CP) {
          return;
        }
        setEditAccountInfo((info) => {
          if (info.level !== LEVEL_TYPE.EU) return info;
          return { ...info, billingType: value as BILLING_TYPE };
        });
      }
    },
    [selectedAccount, props.isMyAccount]
  );

  const [uploadingLogo, setUploadingLogo] = useState<boolean>(false);

  const logoHandleChange = async (e: any) => {
    let file = e.target.files[0]; // 입력받은 file객체
    setUploadingLogo(true);

    // 이미지 resize 옵션 설정 (최대 width을 100px로 지정)
    const options = {
      maxSizeMB: 2,
      //maxWidthOrHeight: 60,
    };
    if (!enableImageTypesOption.includes(file.type)) {
      return;
    }
    try {
      const compressedFile = await imageCompression(file, options);
      setLogoImageFile(compressedFile);
      // resize된 이미지의 url을 받아 fileUrl에 저장
      const promise = imageCompression.getDataUrlFromFile(compressedFile);
      promise.then((result) => {
        setLogoImageUrl(result);
        setUploadingLogo(false);
      });
    } catch (error) {
      console.log(error);
      setUploadingLogo(false);
    }
  };

  const onSave = () => {
    const name = toTrim(editAccountInfo.name);
    const err = getAccountNameValidationError(name);
    if (err) {
      setValidateMsg((msg) => {
        return {
          ...msg,
          name: lang.validateMsg.account[err],
        };
      });
      return;
    }

    if (
      !props.isMyAccount &&
      selectedAccount.accountLevel === LEVEL_TYPE.DW &&
      editAccountInfo.discountTier === DISCOUNT_TIER.CUSTOM &&
      isDiscountEmpty(editAccountInfo.discount)
    ) {
      setValidateMsg((msg) => {
        return {
          ...msg,
          discount: intlString(lang.validateMsg.account.discount.empty),
        };
      });

      return;
    }
    onClickUpdate(editAccountInfo, logoImageFile as File);
  };

  const mutationUpdateAccount = useStatusMutation(putAccountUpdate, {
    onSuccess: (res: any) => {
      if (res.error === 0) {
        notifySuccess(lang.validateMsg.account.modify.success);
        props.onModal?.("edit");
        props.onSuccessEditAccount?.();
        refetch();
        if (props.isMyAccount) {
          updateCurrentAccount();
        }
      } else {
        notifyError(lang.validateMsg.account.modify.fail);
      }
    },
    onError: (err: CustomError) => {
      if (err.response?.data.error === 3008) {
        handleDuplicateAccountName({
          setFn: setValidateMsg,
          validateKey: "name",
          elementId: "dw_account_input_name",
          message: intlString(lang.ServerErrorCode[3008]),
        });
        return;
      }
      handleErrorNotify(err, lang.validateMsg.account.modify.fail);
    },
  });

  const onClickUpdate = useCallback(
    (accountInfo: EditAccountInfo, file: File) => {
      if (!mutationUpdateAccount.idle) return;
      const formData = new FormData();
      if (file !== undefined) {
        formData.append("file", file);
      }
      // creditLimit is not user-editable in this form yet - never send it in the update request.
      // billingType is only editable (and only meaningful) for a CP viewer - never send it otherwise.
      const { creditLimit, billingType, ...rest } = accountInfo;
      const json = JSON.stringify({
        ...rest,
        ...(addBaseLevel === LEVEL_TYPE.CP ? { billingType } : {}),
        name: toTrim(accountInfo.name),
        companyName: toTrim(accountInfo.companyName),
        companyLocation: toTrim(accountInfo.companyLocation),
        accountAdditionalNumber: toTrim(accountInfo.accountAdditionalNumber),
        description: toTrim(accountInfo.description),
        parentLevel:
          selectedAccount.accountLevel === LEVEL_TYPE.DW
            ? "DW"
            : accountInfo.parentLevel,
        use2faAuth: accountInfo.use2faAuth ?? false,
      });
      const blob = new Blob([json], { type: "application/json" });
      formData.append("accountOptionDto", blob);

      mutationUpdateAccount.mutate({
        form: formData, // Account -> UpdateRequest
        accountId: props.accountId,
      });
    },
    [
      mutationUpdateAccount,
      props.accountId,
      addBaseLevel,
      selectedAccount.accountLevel,
    ]
  );

  return (
    <AccountDetailPresenter
      onSave={onSave}
      accountInfo={accountInfo}
      editAccountInfo={editAccountInfo}
      onChangeAccountInfo={onChangeAccountInfo}
      logoHandleChange={logoHandleChange}
      logoImageUrl={logoImageUrl}
      isUpdateLoading={mutationUpdateAccount.loading || loading}
      isUpdateDisabled={!mutationUpdateAccount.idle}
      validateMsg={validateMsg}
      accountAdmins={accountAdmins}
      accountOwnerIsFetching={accountOwnerIsFetching}
      enableTransfer={props.enableTransfer ?? false}
      setValidateMsg={setValidateMsg}
      setEditAccountInfo={setEditAccountInfo}
      onModal={props.onModal}
      isMyAccount={props.isMyAccount}
      uploadingLogo={uploadingLogo}
    />
  );
}
